import React, { useEffect, useState } from "react";
import { api_Routes } from "../../tools/api_Routes";
import { Helper } from "../../tools/Helper";
import {
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DataTable from "./data-table";
import DataTableByOffer from "./data-table-by-offer";
import SwitchButton from "./switch-button";
import DataTableAllOrder from "./data-table_all";
import { errorToaster, successToaster } from "../../helper/toasterConfiguration";

export const OrdersTable = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [orderStatus, setOrderStatus] = useState();
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [source, setSource] = useState();
  const [panelUrl, setPanelUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState("");
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [orderOption, setOrderOption] = useState("all");
  const [resellers, setResellers] = useState([]);
  const [resellerFilter, setResellerFilter] = useState('all');
  const [isLoadingResellers, setIsLoadingResellers] = useState(false);

  // Bulk action states - same pattern as UsersTable
  const [selected, setSelected] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [selectedBulkAction, setSelectedBulkAction] = useState(null);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkPaymentStatus, setBulkPaymentStatus] = useState("");

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const getData = async () => {
    setErrorMessage("");

    if (dateRange.start && !dateRange.end) {
      setErrorMessage("End date is required if a start date is selected.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const queryParams = [];
    if (dateRange.start && dateRange.end) {
      queryParams.push(
          `start_date=${dateRange.start.toISOString().split("T")[0]}`
      );
      queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
    } else if (dateRange.end)
      queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
    if (orderStatus && orderStatus !== "null")
      queryParams.push(`order_status=${orderStatus}`);
    if (paymentStatus && paymentStatus !== "null")
      queryParams.push(`payment_status=${paymentStatus}`);
    if (source && source !== "null") queryParams.push(`source=${source}`);
    if (panelUrl) queryParams.push(`panel_url=${encodeURIComponent(panelUrl)}`);

    // Add reseller filter
    if (resellerFilter && resellerFilter !== 'all') {
      queryParams.push(`reseller=${resellerFilter}`);
    }

    const queryString = queryParams.length ? `${queryParams.join("&")}` : "";

    const url =
        orderOption === "service"
            ? `${
                api_Routes.Orders.View
            }?${queryString}&per_page=${rowsPerPage}&page=${currentPage + 1}`
            :orderOption === "offer" ? `${
                api_Routes.Orders.ViewByOffers
            }?${queryString}&per_page=${rowsPerPage}&page=${currentPage + 1}`
            :`${
                api_Routes.Orders.ViewAll
            }?${queryString}&per_page=${rowsPerPage}&page=${currentPage + 1}`;

    const { response, message } = await Helper.Get({
      url,
      hasToken: true,
    });
    if (response) {
      setData(response);
    } else {
      setData([]);
    }
    setIsLoading(false);
  };

  // Fetch available resellers
  useEffect(() => {
    const fetchResellers = async () => {
      setIsLoadingResellers(true);
      const { response, message } = await Helper.Get({
        url: api_Routes.Resellers.GetAll,
        hasToken: true,
      });
      if (response && response.data) {
        setResellers(response.data);
      }
      setIsLoadingResellers(false);
    };
    fetchResellers();
  }, []);

  // Effect for filter changes
  useEffect(() => {
    if (filtersChanged) {
      setCurrentPage(0);
      setFiltersChanged(false);
    } else {
      getData();
    }
  }, [filtersChanged, currentPage, rowsPerPage, orderOption]);

  const handleResetFilters = () => {
    setCurrentPage(0);
    setDateRange({ start: null, end: null });
    if (orderStatus) setOrderStatus("null");
    if (paymentStatus) setPaymentStatus("null");
    setSource("null");
    setPanelUrl('');
    setResellerFilter('all');
    setFiltersChanged(true);
  };

  // Bulk action handlers - similar to UsersTable
  const handleSelectionChange = (newSelected, newSelectAllMode) => {
    setSelected(newSelected);
    setSelectAllMode(newSelectAllMode);
  };

  const handleBulkActionClick = (action) => {
    setSelectedBulkAction(action);

    if (action === 'change_status' || action === 'change_payment_status') {
      setBulkActionDialogOpen(true);
    }
  };

  const handleExecuteBulkAction = async () => {
    if (selected.size === 0 && !selectAllMode) {
      errorToaster("Please select at least one order");
      return;
    }

    setIsLoading(true);
    try {
      const ids = selectAllMode
          ? 'ALL'
          : Array.from(selected);

      // Build the payload exactly like UsersTable
      const payload = {
        action: selectedBulkAction,
        ids,
        ...(selectedBulkAction === 'change_status' && { status: bulkStatus }),
        ...(selectedBulkAction === 'change_payment_status' && { payment_status: bulkPaymentStatus }),
        // Include filters only when in selectAllMode
        ...(selectAllMode && {
          filters: {
            start_date: dateRange.start ? dateRange.start.toISOString().split("T")[0] : null,
            end_date: dateRange.end ? dateRange.end.toISOString().split("T")[0] : null,
            order_status: orderStatus && orderStatus !== "null" ? orderStatus : null,
            payment_status: paymentStatus && paymentStatus !== "null" ? paymentStatus : null,
            source: source && source !== "null" ? source : null,
            panel_url: panelUrl || null,
            reseller: resellerFilter !== 'all' ? resellerFilter : null,
          }
        }),
      };

      // Use the BulkActions endpoint like UsersTable does
      // Note: You'll need to create this endpoint in your backend
      const endpoint = api_Routes.Orders.BulkActions; // You'll need to add this to your api_Routes

      const { response, message } = await Helper.Post({
        url: endpoint,
        data: payload,
        hasToken: true,
      });

      if (response) {
        successToaster(message || "Bulk action completed successfully");
        // Refresh data
        await getData();
        // Clear selection
        setSelected(new Set());
        setSelectAllMode(false);
        setBulkActionDialogOpen(false);
        setBulkStatus("");
        setBulkPaymentStatus("");
      } else {
        errorToaster(message || "Failed to execute bulk action");
      }
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorToaster("An error occurred while executing bulk action");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseBulkActionDialog = () => {
    setBulkActionDialogOpen(false);
    setBulkStatus("");
    setBulkPaymentStatus("");
  };

  return (
      <div>
        <SwitchButton
            orderOption={orderOption}
            setOrderOption={setOrderOption}
            handleResetFilters={handleResetFilters}
        />

        {/* Bulk Actions Bar - exactly like UsersTable */}
        {(selected.size > 0 || selectAllMode) && (
            <div className="bg-gray-100 p-3 mb-3 rounded flex items-center">
          <span className="mr-4">
            {selectAllMode
                ? `All ${data?.pagination?.total || 0} selected (filtered)`
                : `${selected.size} selected`}
          </span>

              <div className="relative">
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    endIcon={<span className="ml-1">▼</span>}
                    className="mr-2"
                >
                  Bulk Actions
                </Button>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                  <MenuItem onClick={() => {
                    handleBulkActionClick('change_status');
                    setAnchorEl(null);
                  }}>
                    Change Status
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkActionClick('change_payment_status');
                    setAnchorEl(null);
                  }}>
                    Change Payment Status
                  </MenuItem>
                </Menu>
              </div>

              <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelected(new Set());
                    setSelectAllMode(false);
                  }}
              >
                Clear
              </Button>
            </div>
        )}

        {/* Filter Section */}
        <div className="flex flex-col justify-between items-start mb-3 gap-4">
          <div className="w-full flex items-start flex-wrap lg:flex-nowrap justify-between gap-4">
            <TextField
                label="Search for id or panel"
                value={panelUrl}
                onChange={(e) => {
                  setPanelUrl(e.target.value);
                  setFiltersChanged(true);
                }}
                className="w-full"
                fullWidth
            />
            <div className="w-full">
              <LocalizationProvider dateAdapter={AdapterDayjs} className="w-full">
                <DatePicker
                    label="Start Date"
                    value={dateRange.start}
                    className="w-full"
                    onChange={(newValue) => {
                      setDateRange((prev) => ({ ...prev, start: newValue }));
                      setFiltersChanged(true);
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </div>
            <div className="w-full">
              <LocalizationProvider dateAdapter={AdapterDayjs} className="w-full">
                <DatePicker
                    label="End Date"
                    value={dateRange.end}
                    className="w-full"
                    onChange={(newValue) => {
                      setDateRange((prev) => ({ ...prev, end: newValue }));
                      setFiltersChanged(true);
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </div>

            {/* Reseller Filter */}
            <FormControl className="w-full">
              <InputLabel id="reseller-select-label">Reseller</InputLabel>
              <Select
                  labelId="reseller-select-label"
                  id="reseller-select"
                  value={resellerFilter}
                  onChange={(e) => {
                    setResellerFilter(e.target.value);
                    setFiltersChanged(true);
                  }}
                  label="Reseller"
                  sx={{ textAlign: "start" }}
                  disabled={isLoadingResellers}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="website">Website</MenuItem>
                {resellers.map((reseller) => (
                    <MenuItem key={reseller.id} value={reseller.id}>
                      {reseller.name}
                    </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Payment Status Filter */}
            <FormControl className="w-full">
              <InputLabel id="payment-status-select-label">
                Payment Status
              </InputLabel>
              <Select
                  labelId="payment-status-select-label"
                  id="payment-status-select"
                  value={paymentStatus || null}
                  onChange={(e) => {
                    setPaymentStatus(e.target.value);
                    setFiltersChanged(true);
                  }}
                  label="Payment Status"
                  sx={{ textAlign: "start" }}
              >
                <MenuItem value="null">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="cancel">Cancel</MenuItem>
              </Select>
            </FormControl>

            {/* Order Status Filter */}
            <FormControl className="w-full">
              <InputLabel id="order-status-select-label">Order Status</InputLabel>
              <Select
                  labelId="order-status-select-label"
                  id="order-status-select"
                  value={orderStatus || null}
                  onChange={(e) => {
                    setOrderStatus(e.target.value);
                    setFiltersChanged(true);
                  }}
                  label="Order Status"
                  sx={{ textAlign: "start" }}
              >
                <MenuItem value="null">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="cancel">Cancel</MenuItem>
                <MenuItem value="complete">Complete</MenuItem>
              </Select>
            </FormControl>
            <FormControl className="w-full">
              <InputLabel id="order-source-select-label">Order Source</InputLabel>
              <Select
                  labelId="order-source-select-label"
                  id="order-source-select"
                  value={source || null}
                  onChange={(e) => {
                    setSource(e.target.value);
                    setFiltersChanged(true);
                  }}
                  label="Order Source"
                  sx={{ textAlign: "start" }}
              >
                <MenuItem value="null">All</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="external">External</MenuItem>
              </Select>
            </FormControl>
          </div>
          {errorMessage && <div>*{errorMessage}</div>}

          {/* Reset Button */}
          <Button
              variant="outlined"
              color="primary"
              onClick={handleResetFilters}
              disabled={
                !dateRange.start &&
                !dateRange.end &&
                panelUrl === '' &&
                (!orderStatus || orderStatus === "null") &&
                (!paymentStatus || paymentStatus === "null") &&
                (!source || source === "null") &&
                resellerFilter === 'all'
              }
          >
            Reset Filters
          </Button>
        </div>

        {/* Table and Pagination */}
        {isLoading ? (
            <LinearProgress sx={{ marginTop: 1 }} />
        ) : data && data?.data?.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                {orderOption === "service" ? (
                    <DataTable
                        setData={setData}
                        setOrderOption={setOrderOption}
                        data={data?.data}
                        rowsPerPage={rowsPerPage}
                        currentPage={currentPage}
                        filters={{
                          dateRange,
                          orderStatus,
                          paymentStatus,
                          source,
                          panelUrl,
                          resellerFilter
                        }}
                        selected={selected}
                        selectAllMode={selectAllMode}
                        onSelectionChange={handleSelectionChange}
                        totalCount={data?.pagination?.total}
                    />
                ) : orderOption === "offer" ?(
                    <DataTableByOffer
                        setOrderOption={setOrderOption}
                        setData={setData}
                        data={data?.data}
                        rowsPerPage={rowsPerPage}
                        currentPage={currentPage}
                        filters={{
                          dateRange,
                          orderStatus,
                          paymentStatus,
                          source,
                          panelUrl,
                          resellerFilter
                        }}
                        selected={selected}
                        selectAllMode={selectAllMode}
                        onSelectionChange={handleSelectionChange}
                        totalCount={data?.pagination?.total}
                    />
                ):(
                    <DataTableAllOrder
                        setData={setData}
                        setOrderOption={setOrderOption}
                        data={data?.data}
                        rowsPerPage={rowsPerPage}
                        currentPage={currentPage}
                        filters={{
                          dateRange,
                          orderStatus,
                          paymentStatus,
                          source,
                          panelUrl,
                          resellerFilter
                        }}
                        selected={selected}
                        selectAllMode={selectAllMode}
                        onSelectionChange={handleSelectionChange}
                        totalCount={data?.pagination?.total}
                    />
                )}
              </div>
              <div className="mt-4 flex justify-center items-center">
                <TablePagination
                    page={currentPage}
                    className="mt-auto"
                    onPageChange={handlePageChange}
                    count={data.pagination.total}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    variant="outlined"
                    shape="rounded"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </div>
            </>
        ) : (
            <TableContainer>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "primary.main",
                          color: "white",
                          fontWeight: "bold",
                        }}
                    >
                      There are no data yet
                    </TableCell>
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
        )}

        {/* Bulk Action Dialog */}
        <Dialog open={bulkActionDialogOpen} onClose={handleCloseBulkActionDialog}>
          <DialogTitle>
            {selectedBulkAction === 'change_status' ? 'Change Status' : 'Change Payment Status'}
          </DialogTitle>
          <DialogContent>
            {selectedBulkAction === 'change_status' ? (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>New Status</InputLabel>
                  <Select
                      value={bulkStatus}
                      label="New Status"
                      onChange={(e) => setBulkStatus(e.target.value)}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="complete">Complete</MenuItem>
                    <MenuItem value="cancel">Cancel</MenuItem>
                  </Select>
                </FormControl>
            ) : (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>New Payment Status</InputLabel>
                  <Select
                      value={bulkPaymentStatus}
                      label="New Payment Status"
                      onChange={(e) => setBulkPaymentStatus(e.target.value)}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="cancel">Cancel</MenuItem>
                  </Select>
                </FormControl>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBulkActionDialog}>Cancel</Button>
            <Button
                onClick={handleExecuteBulkAction}
                variant="contained"
                disabled={!bulkStatus && !bulkPaymentStatus}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </div>
  );
};